<template>
  <div class="performance-report-container">
    <!-- Modal for date range validation error -->
    <ModalBox 
      :visible="showModal" 
      :infoTitle="modalTitle"  
      :infoMessage="modalMessage" 
      @close="showModal = false">
    </ModalBox>
    
    <div class="report-content">
      <!-- Header Section -->
      <div class="report-header">
        <h2 class="report-title">Performance Metrics Report</h2>
        <p class="report-subtitle">Comprehensive energy analysis and cost optimization insights</p>
      </div>

      <!-- Configuration Section -->
      <div class="config-section">
        <div class="config-row-triple">
          <!-- Plant Selection -->
          <div class="plant-picker-container">
            <label for="plantSelect">Select Plant:</label>
            <select 
              id="plantSelect"
              v-model="selectedPlantId" 
              @change="onPlantChange"
              class="plant-select"
              :disabled="isLoadingPlants"
            >
              <option value="" disabled>
                {{ isLoadingPlants ? 'Loading plants...' : 'Select a plant' }}
              </option>
              <option 
                v-for="plant in availablePlants" 
                :key="plant.plant_id" 
                :value="plant.plant_id"
              >
                {{ plant.alias }} (ID: {{ plant.plant_id }})
              </option>
            </select>
          </div>
          
          <div class="date-picker-container">
            <label for="startDate">Analysis Start Date:</label>
            <input 
              type="date" 
              id="startDate"
              v-model="startDate" 
              @change="onDateChange"
              :max="endDate"
              class="date-input"
              :disabled="!selectedPlantId"
            />
          </div>
          
          <div class="date-picker-container">
            <label for="endDate">Analysis End Date:</label>
                <input 
              type="date" 
              id="endDate"
              v-model="endDate" 
              @change="onDateChange"
              :min="startDate"
              :max="today"
              class="date-input"
              :disabled="!selectedPlantId"
            />
          </div>
            </div>
          </div>
          
      <!-- Summary Cards Section -->
      <div class="summary-section">
        <h4>Key Performance Indicators</h4>
        
        <!-- Loading State for KPIs -->
        <div v-if="isLoading" class="kpi-loading-grid">
          <div v-for="n in 4" :key="n" class="kpi-loading-card">
            <div class="kpi-loading-icon">
              <div class="loading-pulse"></div>
              </div>
            <div class="kpi-loading-content">
              <div class="loading-bar loading-value"></div>
              <div class="loading-bar loading-unit"></div>
              <div class="loading-bar loading-label"></div>
              <div class="loading-bar loading-desc"></div>
              </div>
            </div>
            </div>
        
        <!-- Actual KPI Data -->
        <div v-else class="kpi-grid">
          <!-- Total Consumption KPI -->
          <div class="custom-kpi-card consumption-card">
            <div class="kpi-icon">
              <i class="icon-energy">⚡</i>
          </div>
            <div class="kpi-content">
              <div class="kpi-value">{{ summaryData.totalConsumption }}</div>
              <div class="kpi-unit">kWh</div>
              <div class="kpi-label">Total Period Consumption</div>
              <div class="kpi-description">Total energy consumed</div>
        </div>
      </div>

          <!-- Total Savings KPI -->
          <div class="custom-kpi-card savings-card">
            <div class="kpi-icon">
              <i class="icon-savings">🧾</i>
            </div>
            <div class="kpi-content">
              <div class="kpi-value">${{ summaryData.totalSavings }}</div>
              <div class="kpi-unit">AUD</div>
              <div class="kpi-label">Total Savings</div>
              <div class="kpi-description">Cumulative cost reduction</div>
            </div>
          </div>

          <!-- Equivalent per kWh Price KPI -->
          <div class="custom-kpi-card daily-card">
            <div class="kpi-icon">
              <i class="icon-daily">📈</i>
            </div>
            <div class="kpi-content">
              <div class="kpi-value">${{ summaryData.equivalentPerKwh }}</div>
              <div class="kpi-unit">AUD/kWh</div>
              <div class="kpi-label">Equivalent per kWh Price</div>
              <div class="kpi-description">Average cost per unit consumed</div>
            </div>
          </div>

          <!-- Optimization Rate KPI -->
          <div class="custom-kpi-card optimization-card">
            <div class="kpi-icon">
              <i class="icon-optimization">🧮</i>
            </div>
            <div class="kpi-content">
              <div class="kpi-value">{{ summaryData.optimizationRate }}</div>
              <div class="kpi-unit">%</div>
              <div class="kpi-label">Optimization Rate</div>
              <div class="kpi-description">Average savings percentage</div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Data Message -->
      <div v-if="!isLoading && analyticsData.length === 0" class="no-data-section">
        <div class="no-data-card">
          <div class="no-data-icon">📊</div>
          <h3>No Data Available</h3>
          <p>No performance data was found for the selected date range.</p>
          <p class="no-data-suggestion">Try selecting a different date range or check if data exists for your plant.</p>
        </div>
      </div>

      <!-- Charts Section -->
      <div v-if="!isLoading && analyticsData.length > 0" class="charts-section">
        <!-- Daily Performance Chart -->
        <div class="chart-container">
          <h5>Daily Performance Trends</h5>
          <div v-if="isLoading" class="chart-loading">
            <div class="chart-loading-animation">
              <div class="loading-bars">
                <div v-for="n in 8" :key="n" class="loading-bar-item" :style="{ animationDelay: n * 0.1 + 's' }"></div>
              </div>
              <p class="loading-text">Loading chart data...</p>
            </div>
          </div>
          <canvas v-else id="dailyPerformanceChart"></canvas>
        </div>

        <!-- Monthly Savings Chart -->
        <div class="chart-container">
          <h5>Monthly Savings</h5>
          <div v-if="isLoading" class="chart-loading">
            <div class="chart-loading-animation">
              <div class="loading-bars">
                <div v-for="n in 6" :key="n" class="loading-bar-item" :style="{ animationDelay: n * 0.12 + 's' }"></div>
              </div>
              <p class="loading-text">Calculating savings...</p>
            </div>
          </div>
          <canvas v-else id="monthlySavingsChart"></canvas>
        </div>

        <!-- Daily Savings Chart -->
        <div class="chart-container">
          <h5>Daily Savings</h5>
          <div v-if="isLoading" class="chart-loading">
            <div class="chart-loading-animation">
              <div class="loading-bars">
                <div v-for="n in 12" :key="n" class="loading-bar-item" :style="{ animationDelay: n * 0.08 + 's' }"></div>
              </div>
              <p class="loading-text">Loading daily savings data...</p>
            </div>
          </div>
          <canvas v-else id="dailySavingsChart"></canvas>
        </div>

        <!-- Top Earning Days Chart -->
        <div class="chart-container">
          <h5>Top 10 Highest Saving Days (Sorted High to Low)</h5>
          <div v-if="isLoading" class="chart-loading">
            <div class="chart-loading-animation">
              <div class="loading-bars">
                <div v-for="n in 10" :key="n" class="loading-bar-item" :style="{ animationDelay: n * 0.08 + 's' }"></div>
        </div>
              <p class="loading-text">Finding top performers...</p>
            </div>
          </div>
          <canvas v-else id="topEarningDaysChart"></canvas>
        </div>

        <!-- Monthly Aggregation Chart -->
        <div class="chart-container">
          <h5>Monthly Energy Consumption</h5>
          <div v-if="isLoading" class="chart-loading">
            <div class="chart-loading-animation">
              <div class="loading-bars">
                <div v-for="n in 6" :key="n" class="loading-bar-item" :style="{ animationDelay: n * 0.15 + 's' }"></div>
              </div>
              <p class="loading-text">Aggregating monthly data...</p>
            </div>
          </div>
          <canvas v-else id="monthlyAnalysisChart"></canvas>
        </div>

        <!-- Daily Consumption Chart -->
        <div class="chart-container">
          <h5>Daily Energy Consumption</h5>
          <div v-if="isLoading" class="chart-loading">
            <div class="chart-loading-animation">
              <div class="loading-bars">
                <div v-for="n in 12" :key="n" class="loading-bar-item" :style="{ animationDelay: n * 0.07 + 's' }"></div>
              </div>
              <p class="loading-text">Loading consumption data...</p>
            </div>
          </div>
          <canvas v-else id="dailyConsumptionChart"></canvas>
        </div>

      </div>

      <!-- Detailed Analysis Table -->
      <div v-if="!isLoading && analyticsData.length > 0" class="analysis-section">
        <h4>Detailed Analysis</h4>
        <div class="table-container">
          <!-- Loading State for Table -->
          <div v-if="isLoading" class="table-loading">
          <table class="analysis-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Consumption (kWh)</th>
                  <th>Original Cost (AUD)</th>
                  <th>Optimized Cost (AUD)</th>
                  <th>Savings (AUD)</th>
                  <th>Optimization Rate (Savings %)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="n in 8" :key="n" class="table-loading-row">
                  <td><div class="loading-bar table-loading-bar" :style="{ animationDelay: n * 0.1 + 's' }"></div></td>
                  <td><div class="loading-bar table-loading-bar" :style="{ animationDelay: (n * 0.1 + 0.05) + 's' }"></div></td>
                  <td><div class="loading-bar table-loading-bar" :style="{ animationDelay: (n * 0.1 + 0.1) + 's' }"></div></td>
                  <td><div class="loading-bar table-loading-bar" :style="{ animationDelay: (n * 0.1 + 0.15) + 's' }"></div></td>
                  <td><div class="loading-bar table-loading-bar" :style="{ animationDelay: (n * 0.1 + 0.2) + 's' }"></div></td>
                  <td><div class="loading-bar table-loading-bar" :style="{ animationDelay: (n * 0.1 + 0.25) + 's' }"></div></td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <!-- Actual Table Data -->
          <table v-else class="analysis-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Consumption (kWh)</th>
                <th>Original Cost (AUD)</th>
                <th>Optimized Cost (AUD)</th>
                <th>Savings (AUD)</th>
                <th>Optimization Rate (Savings %)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in analyticsData" :key="index">
                <td>{{ formatDateDisplay(item.date) }}</td>
                <td>{{ item.totalConsumption.toFixed(2) }}</td>
                <td>{{ item.originalCost.toFixed(2) }}</td>
                <td>{{ item.costWithOptimisation.toFixed(2) }}</td>
                <td class="savings-positive">{{ item.savings.toFixed(2) }}</td>
                <td class="savings-positive">{{ ((item.savings / item.originalCost) * 100).toFixed(1) }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Footer Info -->
      <div class="report-footer">
        <div class="info-disclaimer">
          <p><strong>Report Generated:</strong> {{ formatDateDisplay(new Date().toISOString().split('T')[0]) }}</p>
          <p><strong>Analysis Period:</strong> {{ formatDateRange(startDate, endDate) }}</p>
          <p><strong>Data Points:</strong> {{ analyticsData.length }} days</p>
          <p class="disclaimer-text">
            * All calculations based on actual energy consumption and AI-optimized energy management. 
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { Chart, registerables } from 'chart.js';
import { apiRequest } from '@/services/apiService';
import { eventBus } from '@/services/eventBus';
import ModalBox from '@/components/cards/ModalBox.vue';

Chart.register(...registerables);

export default {
  name: 'PerformanceMetricsReport',
  components: {
    ModalBox,
  },
  setup() {
    // Get route to access query parameters
    const route = useRoute();
    
    // Plant selection reactive data
    const availablePlants = ref([]);
    const selectedPlantId = ref('');
    const isLoadingPlants = ref(false);
    
    // Get plant_id from route query parameters (same as DetailPage pattern)
    const urlPlantId = computed(() => route.query.plant_id ? Number(route.query.plant_id) : null);
    
    // Debug: Verify plant_id is being obtained
    // console.log('PerformanceReportPage - Plant ID from route:', urlPlantId.value);
    // console.log('PerformanceReportPage - Full route query:', route.query);
    
    // Reactive data
    const isLoading = ref(false);
    const startDate = ref('');
    const endDate = ref('');
    const today = ref(new Date().toISOString().split('T')[0]);
    const analyticsData = ref([]);
    const chartInstances = ref({});
    let renderTimeout = null;
    let loadDataTimeout = null;
    
    // Modal state
    const showModal = ref(false);
    const modalTitle = ref('');
    const modalMessage = ref('');
    
    // Constants
    const subDataUrl = ref('/api/v1.1/data');

    // Set default dates: 6 months ago to today
    const defaultStartDate = new Date();
    defaultStartDate.setMonth(defaultStartDate.getMonth() - 2);
    startDate.value = defaultStartDate.toISOString().split('T')[0];
    endDate.value = today.value;

    // Computed properties

    const summaryData = computed(() => {
      if (analyticsData.value.length === 0) {
        return {
          totalConsumption: 0,
          totalSavings: 0,
          equivalentPerKwh: 0,
          optimizationRate: 0,
        };
      }

      const totalConsumption = analyticsData.value.reduce((sum, item) => sum + item.totalConsumption, 0);
      const totalSavings = analyticsData.value.reduce((sum, item) => sum + item.savings, 0);
      const totalOriginalCost = analyticsData.value.reduce((sum, item) => sum + item.originalCost, 0);
      const totalOptimizedCost = analyticsData.value.reduce((sum, item) => sum + item.costWithOptimisation, 0);
      
      // Calculate equivalent per kWh price (optimized cost per total consumption)
      const equivalentPerKwh = totalConsumption > 0 ? totalOptimizedCost / totalConsumption : 0;
      const optimizationRate = totalOriginalCost > 0 ? (totalSavings / totalOriginalCost) * 100 : 0;

      return {
        totalConsumption: totalConsumption.toFixed(2),
        totalSavings: totalSavings.toFixed(2),
        equivalentPerKwh: equivalentPerKwh.toFixed(3),
        optimizationRate: optimizationRate.toFixed(1),
      };
    });

    // Methods
    const formatDateDisplay = (dateString) => {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    const formatDateRange = (startDateStr, endDateStr) => {
      return `${formatDateDisplay(startDateStr)} to ${formatDateDisplay(endDateStr)}`;
    };

    const validateDateRange = (startDateStr, endDateStr) => {
      const start = new Date(startDateStr);
      const end = new Date(endDateStr);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays > 365) {
        modalTitle.value = 'Date Range Error';
        modalMessage.value = `Date range exceeds maximum limit of 365 days. Current range: ${diffDays} days. Please select a shorter date range.`;
        showModal.value = true;
        return false;
      }
      
      return true;
    };


    const fetchAvailablePlants = async () => {
      isLoadingPlants.value = true;
      try {
        const url = `${subDataUrl.value}/ai_pool_users/get`;
        const response = await apiRequest('post', url, {});
        
        if (response && response.data && Array.isArray(response.data)) {
          availablePlants.value = response.data.map(user => ({
            plant_id: user.plant_id,
            alias: user.alias,
          }));
          
          // If there's a plant_id in URL, try to select it
          if (urlPlantId.value) {
            const matchingPlant = availablePlants.value.find(plant => plant.plant_id === urlPlantId.value);
            if (matchingPlant) {
              selectedPlantId.value = matchingPlant.plant_id;
            } else {
              console.warn('Plant ID from URL not found in available plants:', urlPlantId.value);
            }
          }
          
          return availablePlants.value;
        } else {
          console.error('Invalid response format for plants data');
          return [];
        }
      } catch (error) {
        console.error('Error fetching available plants:', error);
        modalTitle.value = 'Error Loading Plants';
        modalMessage.value = 'Failed to load available plants. Please refresh the page and try again.';
        showModal.value = true;
        return [];
      } finally {
        isLoadingPlants.value = false;
      }
    };

    const getPerformanceMetricsBulk = async (startDateStr, endDateStr) => {
      // Validate plant_id is available
      if (!selectedPlantId.value || isNaN(selectedPlantId.value)) {
        console.error('Invalid or missing plant_id:', selectedPlantId.value);
        return [];
      }
      
      // Validate date range (365 days max)
      if (!validateDateRange(startDateStr, endDateStr)) {
        return [];
      }
      
      try {
        const bodyData = {
          start_date: startDateStr,
          end_date: endDateStr,
          plant_id: selectedPlantId.value,
        };
        const url = `${subDataUrl.value}/performance_metrics/bulk/get`;
        
        // Debug: Log API request details
        // console.log(`Bulk API Request:`, {
        //   url: url,
        //   method: 'POST',
        //   body: bodyData
        // });
        
        const response = await apiRequest('post', url, bodyData);
        
        // console.log(`Bulk API Response:`, response); // Debug log
        
        if (response && response.errorCode === 0 && response.data && Array.isArray(response.data)) {
          const processedData = response.data.map(item => {
            // Validate that we have the required fields
            if (item.total_consumption !== undefined && item.original_cost !== undefined && item.cost_with_optimisation !== undefined) {
              // Extract date from ISO string (e.g., "2025-09-01T10:00:00+10:00" -> "2025-09-01")
              const dateOnly = item.date.split('T')[0];
              
              return {
                date: dateOnly,
                totalConsumption: parseFloat(item.total_consumption) || 0,
                usageCost: parseFloat(item.usage_cost) || 0,
                feedInCost: parseFloat(item.feed_in_cost) || 0,
                originalCost: parseFloat(item.original_cost) || 0,
                costWithOptimisation: parseFloat(item.cost_with_optimisation) || 0,
                savings: parseFloat((item.original_cost - item.cost_with_optimisation).toFixed(2)) || 0,
              };
            } else {
              console.warn(`Invalid data structure for item:`, item);
              return null;
            }
          }).filter(item => item !== null); // Remove invalid items
          
          // console.log(`Processed bulk data:`, processedData.length, 'records'); // Debug log
          return processedData;
        }
        
        // No data available
        console.log(`No data available for date range ${startDateStr} to ${endDateStr}`);
        return [];
        
      } catch (error) {
        console.error('Error fetching bulk performance metrics data:', error);
        return [];
      }
    };


    const loadHistoricalData = async () => {
      // Don't load data if no plant is selected
      if (!selectedPlantId.value) {
        console.log('No plant selected, skipping data load');
        analyticsData.value = [];
        return;
      }
      
      isLoading.value = true;
      
      try {
        // console.log(`Fetching bulk data from ${startDate.value} to ${endDate.value}...`);
        // Force refresh - removed generateDateRange function
        
        // Single bulk API call to get all data for the date range
        const bulkResults = await getPerformanceMetricsBulk(startDate.value, endDate.value);
        
        // console.log(`Bulk API returned: ${bulkResults.length} records`); // Debug log
        
        // Sort results by date to ensure consistent ordering
        const sortedResults = bulkResults.sort((a, b) => new Date(a.date) - new Date(b.date));
        
        // Update the reactive data with the complete dataset
        if (sortedResults.length === 0) {
          console.warn('No valid data received from bulk API for the date range');
          analyticsData.value = [];
        } else {
          // console.log('Sample result:', sortedResults[0]); // Debug log
          // console.log('Setting analytics data with complete dataset:', sortedResults.length, 'records');
          analyticsData.value = sortedResults; // Set all data at once
        }
        
        // Wait for Vue to update the DOM with the new data
        await nextTick();
        
        // Only render charts after all data is loaded and DOM is updated
        if (analyticsData.value.length > 0) {
          // console.log('Rendering charts with complete dataset');
          renderCharts();
        }
        
      } catch (error) {
        console.error('Error loading historical data:', error);
        analyticsData.value = []; // Clear data on error
      } finally {
        isLoading.value = false;
      }
    };


    const onDateChange = () => {
      // Debounce data loading to prevent rapid consecutive calls
      if (loadDataTimeout) {
        clearTimeout(loadDataTimeout);
      }
      
      loadDataTimeout = setTimeout(() => {
        console.log('Date changed, reloading data...');
        loadHistoricalData();
      }, 300); // 300ms debounce
    };

    const onPlantChange = () => {
      console.log('Plant changed to:', selectedPlantId.value);
      // Clear existing data and reload with new plant
      analyticsData.value = [];
      destroyCharts();
      
      if (selectedPlantId.value) {
        loadHistoricalData();
      }
    };


    const destroyCharts = () => {
      Object.values(chartInstances.value).forEach(chart => {
        if (chart && typeof chart.destroy === 'function') {
          try {
            chart.destroy();
          } catch (error) {
            console.warn('Error destroying chart:', error);
          }
        }
      });
      chartInstances.value = {};
    };

    const renderCharts = () => {
      // Clear any pending render
      if (renderTimeout) {
        clearTimeout(renderTimeout);
      }
      
      renderTimeout = setTimeout(() => {
        destroyCharts();
        
        // console.log('Rendering charts with data:', analyticsData.value.length, 'records'); // Debug log
        
        if (analyticsData.value.length === 0) {
          console.log('No data available for charts - API returned no valid data'); // Debug log
          return;
        }
        
        // Ensure we have stable data before rendering
        // console.log('Final dataset summary:', {
        //   totalRecords: analyticsData.value.length,
        //   dateRange: analyticsData.value.length > 0 ? {
        //     start: analyticsData.value[0].date,
        //     end: analyticsData.value[analyticsData.value.length - 1].date
        //   } : null,
        //   totalConsumption: analyticsData.value.reduce((sum, item) => sum + item.totalConsumption, 0).toFixed(2),
        //   totalSavings: analyticsData.value.reduce((sum, item) => sum + item.savings, 0).toFixed(2)
        // });

        // Daily Performance Chart
        try {
          const dailyCtx = document.getElementById('dailyPerformanceChart');
          if (dailyCtx && dailyCtx.getContext) {
          // console.log('Rendering daily performance chart'); // Debug log
          chartInstances.value.daily = new Chart(dailyCtx, {
          type: 'line',
          data: {
            labels: analyticsData.value.map(item => formatDateDisplay(item.date)),
            datasets: [
              {
                label: 'Original Cost (AUD)',
                data: analyticsData.value.map(item => item.originalCost),
                borderColor: '#ff6b6b',
                backgroundColor: 'rgba(255, 107, 107, 0.1)',
                fill: false,
                tension: 0.1,
              },
              {
                label: 'Optimized Cost (AUD)',
                data: analyticsData.value.map(item => item.costWithOptimisation),
                borderColor: '#4ecdc4',
                backgroundColor: 'rgba(78, 205, 196, 0.1)',
                fill: false,
                tension: 0.1,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top',
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Cost (AUD)',
                },
              },
            },
          },
        });
        }
      } catch (error) {
        console.error('Error creating daily performance chart:', error);
      }

      // Calculate monthly data once for both charts
      const monthlyData = aggregateMonthlyData();
      // console.log('Monthly data:', monthlyData); // Debug log

      // Monthly Analysis Chart
      try {
        const monthlyCtx = document.getElementById('monthlyAnalysisChart');
        if (monthlyCtx && monthlyCtx.getContext && monthlyData.length > 0) {
        // console.log('Rendering monthly analysis chart'); // Debug log
        chartInstances.value.monthly = new Chart(monthlyCtx, {
          type: 'bar',
          data: {
            labels: monthlyData.map(item => item.month),
            datasets: [
              {
                label: 'Total Consumption (kWh)',
                data: monthlyData.map(item => item.consumption),
                backgroundColor: 'rgba(54, 162, 235, 0.7)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1,
                maxBarThickness: 40, // Limit bar thickness
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top',
              },
              tooltip: {
                callbacks: {
                  afterLabel: function(context) {
                    const monthData = monthlyData[context.dataIndex];
                    return [
                      `Original Cost: $${monthData.originalCost.toFixed(2)}`,
                      `Optimized Cost: $${monthData.optimizedCost.toFixed(2)}`,
                      `Savings: $${monthData.savings.toFixed(2)}`
                    ];
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Energy Consumption (kWh)',
                },
              },
              x: {
                title: {
                  display: true,
                  text: 'Month',
                },
              },
            },
          },
        });
      }
      } catch (error) {
        console.error('Error creating monthly analysis chart:', error);
      }

      // Monthly Savings Chart
      try {
        const monthlySavingsCtx = document.getElementById('monthlySavingsChart');
        // console.log('Monthly savings chart context:', monthlySavingsCtx); // Debug log
        // console.log('Monthly data for savings chart:', monthlyData); // Debug log
        if (monthlySavingsCtx && monthlySavingsCtx.getContext) {
          console.log('Rendering monthly savings chart'); // Debug log
          
          const chartData = monthlyData.length > 0 ? {
            labels: monthlyData.map(item => item.month),
              datasets: [
                {
                  label: 'Monthly Savings (AUD)',
                  data: monthlyData.map(item => item.savings),
                  backgroundColor: 'rgba(34, 197, 94, 0.8)',
                  borderColor: 'rgba(34, 197, 94, 1)',
              borderWidth: 2,
                  borderRadius: 6,
                  maxBarThickness: 40, // Limit bar thickness
                },
              ],
          } : {
            labels: ['No Data'],
            datasets: [
              {
                label: 'Monthly Savings (AUD)',
                data: [0],
                backgroundColor: 'rgba(34, 197, 94, 0.3)',
                borderColor: 'rgba(34, 197, 94, 0.6)',
                borderWidth: 2,
                maxBarThickness: 40, // Limit bar thickness
              },
            ],
          };

          chartInstances.value.monthlySavings = new Chart(monthlySavingsCtx, {
            type: 'bar',
            data: chartData,
          options: {
            responsive: true,
            datasets: {
              bar: {
                maxBarThickness: 40, // Limit bar thickness
              }
            },
            plugins: {
              legend: {
                  position: 'top',
                },
                tooltip: {
                  callbacks: {
                    afterLabel: function(context) {
                      if (monthlyData.length > 0) {
                        const monthData = monthlyData[context.dataIndex];
                        return `Original Cost: $${monthData.originalCost.toFixed(2)}`;
                      }
                      return 'No data available';
                    }
                  }
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Savings (AUD)',
                  },
                },
                x: {
                  title: {
                    display: true,
                    text: 'Month',
                  },
                },
            },
          },
        });
        } else {
          console.log('Monthly savings chart not rendered. Context:', !!monthlySavingsCtx, 'Has getContext:', monthlySavingsCtx?.getContext);
      }
      } catch (error) {
        console.error('Error creating monthly savings chart:', error);
      }

      // Top Earning Days Chart
      try {
        const topEarningCtx = document.getElementById('topEarningDaysChart');
        if (topEarningCtx && topEarningCtx.getContext) {
          console.log('Rendering top earning days chart'); // Debug log
          
          const topEarningDays = getTopEarningDays();
          
          chartInstances.value.topEarning = new Chart(topEarningCtx, {
          type: 'bar',
          data: {
              labels: topEarningDays.map(item => formatDateDisplay(item.date)),
            datasets: [
              {
                  label: 'Daily Savings (AUD)',
                  data: topEarningDays.map(item => item.savings),
                  backgroundColor: 'rgba(34, 197, 94, 0.8)',
                  borderColor: 'rgba(34, 197, 94, 1)',
                  borderWidth: 2,
                  borderRadius: 6,
                  maxBarThickness: 30, // Limit bar thickness (thinner for daily data)
                },
              ],
            },
            options: {
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
                tooltip: {
                  callbacks: {
                    afterLabel: function(context) {
                      const dayData = topEarningDays[context.dataIndex];
                      return [
                        `Consumption: ${dayData.totalConsumption.toFixed(2)} kWh`,
                        `Original Cost: $${dayData.originalCost.toFixed(2)}`,
                        `Optimized Cost: $${dayData.costWithOptimisation.toFixed(2)}`
                      ];
                    }
                  }
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Daily Savings (AUD)',
                  },
                },
                x: {
                  title: {
                    display: true,
                    text: 'Date',
                  },
                },
              },
            },
          });
        }
      } catch (error) {
        console.error('Error creating top earning days chart:', error);
      }

      // Daily Consumption Chart
      try {
        const dailyConsumptionCtx = document.getElementById('dailyConsumptionChart');
        if (dailyConsumptionCtx && dailyConsumptionCtx.getContext) {
          console.log('Rendering daily consumption chart'); // Debug log
          
          chartInstances.value.dailyConsumption = new Chart(dailyConsumptionCtx, {
            type: 'bar',
            data: {
              labels: analyticsData.value.map(item => formatDateDisplay(item.date)),
              datasets: [
                {
                  label: 'Daily Consumption (kWh)',
                  data: analyticsData.value.map(item => item.totalConsumption),
                  backgroundColor: 'rgba(59, 130, 246, 0.8)',
                  borderColor: 'rgba(59, 130, 246, 1)',
                  borderWidth: 2,
                  borderRadius: 4,
                  maxBarThickness: 20, // Limit bar thickness (thinnest for daily data with many points)
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top',
              },
                tooltip: {
                  callbacks: {
                    afterLabel: function(context) {
                      const dayData = analyticsData.value[context.dataIndex];
                      return [
                        `Original Cost: $${dayData.originalCost.toFixed(2)}`,
                        `Optimized Cost: $${dayData.costWithOptimisation.toFixed(2)}`,
                        `Savings: $${dayData.savings.toFixed(2)}`
                      ];
                    }
                  }
                }
            },
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                    text: 'Energy Consumption (kWh)',
                  },
                },
                x: {
                  title: {
                    display: true,
                    text: 'Date',
                },
              },
            },
          },
        });
      }
      } catch (error) {
        console.error('Error creating daily consumption chart:', error);
      }

      // Daily Savings Chart
      try {
        const dailySavingsCtx = document.getElementById('dailySavingsChart');
        if (dailySavingsCtx && dailySavingsCtx.getContext) {
          console.log('Rendering daily savings chart'); // Debug log
          
          chartInstances.value.dailySavings = new Chart(dailySavingsCtx, {
            type: 'bar',
            data: {
              labels: analyticsData.value.map(item => formatDateDisplay(item.date)),
              datasets: [
                {
                  label: 'Daily Savings (AUD)',
                  data: analyticsData.value.map(item => item.savings),
                  backgroundColor: 'rgba(34, 197, 94, 0.8)',
                  borderColor: 'rgba(34, 197, 94, 1)',
                  borderWidth: 2,
                  borderRadius: 4,
                  maxBarThickness: 20, // Limit bar thickness (thinnest for daily data with many points)
                },
              ],
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'top',
                },
                tooltip: {
                  callbacks: {
                    afterLabel: function(context) {
                      const dayData = analyticsData.value[context.dataIndex];
                      return [
                        `Consumption: ${dayData.totalConsumption.toFixed(2)} kWh`,
                        `Original Cost: $${dayData.originalCost.toFixed(2)}`,
                        `Optimized Cost: $${dayData.costWithOptimisation.toFixed(2)}`
                      ];
                    }
                  }
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Daily Savings (AUD)',
                  },
                },
                x: {
                  title: {
                    display: true,
                    text: 'Date',
                  },
                },
              },
            },
          });
        }
      } catch (error) {
        console.error('Error creating daily savings chart:', error);
      }

      }, 50); // Debounce with 50ms delay
    };

    const aggregateMonthlyData = () => {
      const monthlyMap = new Map();
      
      analyticsData.value.forEach(item => {
        const date = new Date(item.date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!monthlyMap.has(monthKey)) {
          monthlyMap.set(monthKey, {
            month: date.toLocaleDateString('en-AU', { year: 'numeric', month: 'short' }),
            consumption: 0,
            savings: 0,
            originalCost: 0,
            optimizedCost: 0,
          });
        }
        
        const monthData = monthlyMap.get(monthKey);
        monthData.consumption += item.totalConsumption;
        monthData.savings += item.savings;
        monthData.originalCost += item.originalCost;
        monthData.optimizedCost += item.costWithOptimisation;
      });
      
      return Array.from(monthlyMap.values());
    };

    const getTopEarningDays = () => {
      // Get top 10 days with highest savings, sorted from high to low (left to right)
      return analyticsData.value
        .filter(item => item.savings > 0)
        .sort((a, b) => b.savings - a.savings) // Sort by savings from highest to lowest
        .slice(0, 10); // Take top 10, keep high-to-low order for chart display
    };

    const handleEnvironmentChange = (isDevMode) => {
      // console.log("isDevMode:", isDevMode);
      subDataUrl.value = isDevMode ? "/dev-api/api/v1.1/data" : "/api/v1.1/data";
      // Trigger a refresh of the data when environment changes
      loadHistoricalData();
    };

    // Lifecycle
    onMounted(async () => {
      // First fetch available plants
      await fetchAvailablePlants();
      
      // Then load data if a plant was auto-selected from URL
      if (selectedPlantId.value) {
        loadHistoricalData();
      }
      
      // Listen for environment changes
      eventBus.on('environment-changed', handleEnvironmentChange);
    });

    onUnmounted(() => {
      if (renderTimeout) {
        clearTimeout(renderTimeout);
      }
      if (loadDataTimeout) {
        clearTimeout(loadDataTimeout);
      }
      destroyCharts();
      // Remove the event listener when component is destroyed
      eventBus.off('environment-changed', handleEnvironmentChange);
    });

    return {
      isLoading,
      startDate,
      endDate,
      today,
      analyticsData,
      summaryData,
      formatDateDisplay,
      formatDateRange,
      onDateChange,
      onPlantChange,
      showModal,
      modalTitle,
      modalMessage,
      availablePlants,
      selectedPlantId,
      isLoadingPlants,
    };
  },
};
</script>

<style scoped>
.performance-report-container {
  width: 100%;
  margin: 0;
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.report-header {
  text-align: center;
  margin-bottom: 30px;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px;
}

.report-title {
  font-size: 2.2rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.report-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
  margin: 0;
}

.config-section {
  background: var(--bg-secondary);
  padding: 25px;
  border-radius: 12px;
  margin-bottom: 30px;
  border-left: 4px solid #4ecdc4;
}

.config-row-triple {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 30px;
  align-items: start;
  width: 100%;
  margin: 0;
}

.plant-picker-container label,
.date-picker-container label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--text-primary);
}

.plant-select {
  width: 100%;
  padding: 12px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  background-color: var(--input-bg);
  color: var(--text-primary);
  transition: border-color 0.3s ease;
  cursor: pointer;
}

.plant-select:focus {
  outline: none;
  border-color: #4ecdc4;
  box-shadow: 0 0 0 3px rgba(78, 205, 196, 0.1);
}

.plant-select:disabled {
  background-color: var(--bg-secondary);
  color: var(--text-tertiary);
  cursor: not-allowed;
  opacity: 0.6;
}

.date-input {
  width: 100%;
  padding: 12px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  background-color: var(--input-bg);
  color: var(--text-primary);
}

.date-input:focus {
  outline: none;
  border-color: #4ecdc4;
  box-shadow: 0 0 0 3px rgba(78, 205, 196, 0.1);
}

/* Loading Animations */
@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes loading-bar-animation {
  0%, 40%, 100% {
    transform: scaleY(0.4);
    opacity: 0.6;
  }
  20% {
    transform: scaleY(1);
    opacity: 1;
  }
}

@keyframes loading-pulse-animation {
  0%, 100% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
}


.summary-section {
  margin-bottom: 40px;
}

.summary-section h4 {
  margin-bottom: 20px;
  color: var(--text-primary);
  font-weight: 600;
}

.kpi-grid, .kpi-loading-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 25px;
  width: 100%;
  margin: 0;
}

/* KPI Loading States */
.kpi-loading-card {
  background: var(--card-bg);
  border-radius: 20px;
  padding: 32px 28px;
  display: flex;
  align-items: center;
  gap: 24px;
  box-shadow: 0 12px 40px var(--shadow-color);
  border: 1px solid var(--border-color);
  min-height: 140px;
  position: relative;
  overflow: hidden;
}

.kpi-loading-icon {
  width: 80px;
  height: 80px;
  border-radius: 18px;
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.loading-pulse {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #a0aec0 0%, #718096 100%);
  animation: loading-pulse-animation 1.5s ease-in-out infinite;
}

.kpi-loading-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.loading-bar {
  background: linear-gradient(90deg, #e2e8f0 25%, #f7fafc 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
  border-radius: 4px;
}

.loading-value {
  height: 28px;
  width: 60%;
}

.loading-unit {
  height: 14px;
  width: 30%;
}

.loading-label {
  height: 16px;
  width: 80%;
}

.loading-desc {
  height: 12px;
  width: 70%;
}

/* Custom KPI Card Styles */
.custom-kpi-card {
  background: var(--card-bg);
  border-radius: 20px;
  padding: 32px 28px;
  display: flex;
  align-items: center;
  gap: 24px;
  box-shadow: 0 12px 40px var(--shadow-color);
  border: 1px solid var(--border-color);
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  min-height: 140px;
}

.custom-kpi-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(90deg, var(--card-accent), var(--card-accent-light));
  border-radius: 20px 20px 0 0;
}

.custom-kpi-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 60px rgba(0, 0, 0, 0.15);
}

.consumption-card {
  --card-accent: #667eea;
  --card-accent-light: #764ba2;
  --card-accent-rgb: 102, 126, 234;
}

.savings-card {
  --card-accent: #4ecdc4;
  --card-accent-light: #44a08d;
  --card-accent-rgb: 78, 205, 196;
}

.daily-card {
  --card-accent: #f093fb;
  --card-accent-light: #f5576c;
  --card-accent-rgb: 240, 147, 251;
}

.optimization-card {
  --card-accent: #00f2fe;
  --card-accent-light: #4facfe;
  --card-accent-rgb: 79, 172, 254;
}

.kpi-icon {
  background: linear-gradient(135deg, var(--card-accent), var(--card-accent-light));
  border-radius: 18px;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: white;
  box-shadow: 
    0 8px 24px rgba(0, 0, 0, 0.2),
    0 0 20px rgba(var(--card-accent-rgb), 0.3);
  position: relative;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.custom-kpi-card:hover .kpi-icon {
  transform: scale(1.05);
  box-shadow: 
    0 12px 32px rgba(0, 0, 0, 0.25),
    0 0 30px rgba(var(--card-accent-rgb), 0.4);
}

.kpi-content {
  flex: 1;
}

.kpi-value {
  font-size: 2.2rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
  margin-bottom: 6px;
  letter-spacing: -0.3px;
}

.kpi-unit {
  font-size: 1rem;
  color: var(--card-accent);
  font-weight: 700;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.kpi-label {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-secondary);
  margin-bottom: 6px;
  line-height: 1.2;
}

.kpi-description {
  font-size: 0.9rem;
  color: var(--text-tertiary);
  line-height: 1.4;
  font-weight: 500;
}


.charts-section {
  margin-bottom: 40px;
}

/* Chart Loading States */
.chart-loading {
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-secondary);
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}

.chart-loading-animation {
  text-align: center;
}

.loading-bars {
  display: flex;
  align-items: end;
  justify-content: center;
  gap: 8px;
  height: 100px;
  margin-bottom: 20px;
}

.loading-bar-item {
  width: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 6px 6px 0 0;
  animation: loading-bar-animation 1.2s ease-in-out infinite;
  min-height: 20px;
}

.loading-text {
  color: var(--text-tertiary);
  font-size: 0.9rem;
  font-weight: 500;
  margin: 0;
  animation: loading-pulse-animation 2s ease-in-out infinite;
}

.chart-container {
  background: var(--card-bg);
  padding: 25px;
  border-radius: 12px;
  margin-bottom: 25px;
  box-shadow: 0 2px 10px var(--shadow-color);
  border: 1px solid var(--border-color);
  height: 550px; /* Set fixed height (70% of ~500px) */
}

.chart-container canvas {
  max-height: 370px; /* Chart area height */
}

.chart-container h5 {
  margin-bottom: 20px;
  color: var(--text-primary);
  font-weight: 600;
  padding-bottom: 10px;
  border-bottom: 2px solid var(--border-color);
}

.analysis-section {
  margin-bottom: 30px;
}

.analysis-section h4 {
  margin-bottom: 20px;
  color: var(--text-primary);
  font-weight: 600;
}

.table-container {
  overflow-x: auto;
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow-color);
}

.analysis-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--card-bg);
}

.analysis-table th {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 15px 12px;
  text-align: center;
  font-weight: 600;
  font-size: 0.9rem;
}

.analysis-table td {
  padding: 12px;
  border-bottom: 1px solid var(--border-color);
  font-size: 0.9rem;
  text-align: center;
  color: var(--text-primary);
}

.analysis-table tbody tr:hover {
  background-color: var(--bg-secondary);
}

.savings-positive {
  color: #28a745;
  font-weight: 600;
}

/* Table Loading States */
.table-loading {
  opacity: 0.7;
}

.table-loading-row {
  height: 45px;
}

.table-loading-bar {
  height: 14px;
  width: 80%;
  margin: 0 auto;
  background: linear-gradient(90deg, #e2e8f0 25%, #f7fafc 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.8s ease-in-out infinite;
  border-radius: 3px;
}

/* No Data State */
.no-data-section {
  margin: 40px 0;
  text-align: center;
}

.no-data-card {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px dashed #cbd5e1;
  border-radius: 16px;
  padding: 60px 40px;
  max-width: 500px;
  margin: 0 auto;
}

.no-data-icon {
  font-size: 4rem;
  margin-bottom: 20px;
  opacity: 0.6;
}

.no-data-card h3 {
  color: #475569;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 15px;
}

.no-data-card p {
  color: #64748b;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 10px;
}

.no-data-suggestion {
  font-style: italic;
  color: #94a3b8;
  font-size: 0.9rem;
}


.report-footer {
  background: var(--bg-secondary);
  padding: 25px;
  border-radius: 12px;
  border-top: 3px solid #4ecdc4;
}

.info-disclaimer p {
  margin-bottom: 8px;
  font-size: 0.9rem;
  color: var(--text-secondary);
}

.disclaimer-text {
  font-style: italic;
  color: var(--text-tertiary);
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid var(--border-color);
}

/* Dark mode: Make footer text black */
.dark-mode .info-disclaimer p {
  color: #000000 !important;
}

.dark-mode .disclaimer-text {
  color: #000000 !important;
}

@media (max-width: 1200px) {
  .kpi-grid, .kpi-loading-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .config-row-triple {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .kpi-grid, .kpi-loading-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .custom-kpi-card, .kpi-loading-card {
    padding: 24px 20px;
    min-height: 120px;
  }
  
  .kpi-icon, .kpi-loading-icon {
    width: 70px;
    height: 70px;
    font-size: 28px;
  }
  
  .kpi-value {
    font-size: 1.9rem;
  }
  
  .report-title {
    font-size: 1.8rem;
  }
  
  .analysis-table th,
  .analysis-table td {
    padding: 8px 6px;
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .performance-report-container {
    padding: 15px;
  }
  
  .chart-container {
    padding: 15px;
  }
  
  .report-header {
    padding: 15px;
  }
  
  .config-section {
    padding: 15px;
  }
}
</style>
